<template src="./quiz-configration.html"></template>

<script>
  import appHeader from "../../../shared-module/components/app-header/app-header.vue";
  import quizConfigurationService from "../../services/quiz-configuration-service";
  import courseTopicService from "../../services/course-topic-service";
  import courseSubTopicService from "../../services/course-sub-topic-service";
  import storageService from "../../services/storage-service";
  import _ from "lodash";

  export default {
    name: "app-quiz-configration",
    components: {
      "app-header": appHeader,
    },
    directives: {
      'click-outside': {
        bind(el, binding) {
          el._clickOutsideHandler = (event) => {
            if (!(el === event.target || el.contains(event.target))) {
              binding.value(event);
            }
          };
          document.addEventListener('click', el._clickOutsideHandler);
        },
        unbind(el) {
          document.removeEventListener('click', el._clickOutsideHandler);
        }
      }
    },
    data() {
      return {
        configurations: [],
        isOpen: false,
        currentStep: 1,
        configName: "",
        baseConfiguration: "", // New: Configuration name field
        niches: [], // New: Array of niches from API
        selectedNiches: [], // Changed: Array of selected niche objects for multi-select
        showNicheDropdown: false, // New: Dropdown visibility
        topicInput: "",
        topics: [
          { name: "Neurons", subtopics: ["Motor Neurons", "Sensory Neurons"] },
          { name: "Synapses", subtopics: [] },
        ],
        showSubtopicModal: false,
        selectedTopicIndex: null,
        subtopicInput: '',
        isEditMode: false,
        editingConfigId: null,
        
        // Topic Editing
        editingTopicIndex: null,
        editingTopicName: '',

        // Subtopic Editing
        tempSubtopics: [],
        editingSubtopicIndex: null,
        editingSubtopicName: '',
        isEditingSubtopics: false,
        selectedTopicName: '',
        isLoading: false,
        isSaving: false,

        // Learning Outcomes
        showLearningOutcomeModal: false,
        tempLearningOutcomes: [],
        learningOutcomeInput: '',
        selectedSubtopicIndex: null, // Index within tempSubtopics (modal context) or maybe we need better tracking?
        // Actually, we open LO modal from Subtopic Modal.
        // So we need to track which subtopic we are editing in the tempSubtopics array.
        selectedSubtopicName: '',
        editingLearningOutcomeIndex: null,
        editingLearningOutcomeName: '',
        
        // Equation support for Learning Outcomes
        learningOutcomeEquationMode: false,
        learningOutcomeEquation: '',
        editingLOEquationMode: false,
        editingLOEquation: '',
        
        isFetchingConfig: false,

        // Pagination
        configPage: 1,
        configLimit: 10,
        totalPages: 1,
        totalDocs: 0,
        
        searchQuery: '',

        // FSC scope (optional). Empty = untagged / legacy flows only.
        boardSelectionTree: [],
        selectedBoardIds: [],
        selectedProgramIds: [],
        selectedClasses: [],
        showBoardDropdown: false,
        showProgramDropdown: false,
        showClassDropdown: false,
        // Which topic/subtopic scope picker is open: { index, field: 'board'|'program'|'class' }
        topicScopeDropdown: null,
        subtopicScopeDropdown: null,
        classOptions: [
          { id: '11', label: 'Class 11 (First Year)' },
          { id: '12', label: 'Class 12 (Second Year)' },
        ],

        // CSS niche (id 2) only
        CSS_NICHE_ID: 2,
        CSS_DEFAULT_MARKS: 100,
        cssMarksOptions: [100, 200],
        cssSubjectTypeOptions: [
          { value: 'compulsory', label: 'Compulsory' },
          { value: 'optional', label: 'Optional' },
        ],
        subjectType: 'compulsory',
        marks: 100,
        cssGroup: null,
        cssGroupOptions: [1, 2, 3, 4, 5, 6, 7, 8],
        /** When false, subject is hidden from get-all-CSS-subjects only. Default true. */
        showInSubjects: true,
      };
    },
    computed: {
      isCssSelected() {
        return (this.selectedNiches || []).some((n) => Number(n.id) === this.CSS_NICHE_ID);
      },
      hasNonCssNicheSelected() {
        return (this.selectedNiches || []).some((n) => Number(n.id) !== this.CSS_NICHE_ID);
      },
      flatBoards() {
        const boards = [];
        (this.boardSelectionTree || []).forEach((province) => {
          (province.boards || []).forEach((board) => {
            boards.push({
              id: board.id,
              name: board.name,
              province: province.province,
              programs: board.programs || [],
            });
          });
        });
        return boards;
      },
      availablePrograms() {
        const selected = new Set(this.selectedBoardIds.map(Number));
        const programs = [];
        const seen = new Set();
        this.flatBoards.forEach((board) => {
          if (!selected.has(Number(board.id))) return;
          (board.programs || []).forEach((program) => {
            if (seen.has(Number(program.id))) return;
            seen.add(Number(program.id));
            programs.push({
              id: program.id,
              name: program.name,
              board_id: board.id,
              board_name: board.name,
            });
          });
        });
        return programs;
      },
      selectedBoardLabels() {
        return this.flatBoards
          .filter((b) => this.selectedBoardIds.includes(Number(b.id)) || this.selectedBoardIds.includes(b.id))
          .map((b) => b.name);
      },
      selectedProgramLabels() {
        return this.availablePrograms
          .filter((p) => this.selectedProgramIds.includes(Number(p.id)) || this.selectedProgramIds.includes(p.id))
          .map((p) => p.name);
      },
      selectedClassLabels() {
        return this.classOptions
          .filter((c) => this.selectedClasses.includes(c.id))
          .map((c) => c.label);
      },
      isConfigNameValid() {
        if (!this.baseConfiguration || !this.baseConfiguration.trim()) return false;
        if (!this.selectedNiches || this.selectedNiches.length === 0) return false;
        if (!this.configName) return false;
        const trimmed = this.configName.trim();
        if (trimmed.length < 3) return false;
        // Letters, numbers, spaces, - _ ( ) allowed e.g. Biology (PB-1)
        const validNameRegex = /^[a-zA-Z0-9\s\-_()]+$/;
        if (!validNameRegex.test(trimmed)) return false;

        if (this.isCssSelected) {
          const allowedTypes = this.cssSubjectTypeOptions.map((o) => o.value);
          if (!allowedTypes.includes(this.subjectType)) return false;
          const marksNum = Number(this.marks);
          if (this.subjectType === 'compulsory') {
            if (marksNum !== 100) return false;
          } else if (!this.cssMarksOptions.includes(marksNum)) {
            return false;
          }
          if (this.subjectType === 'optional') {
            const groupNum = Number(this.cssGroup);
            if (!Number.isFinite(groupNum) || groupNum < 1 || groupNum > 8) return false;
          }
        }
        return true;
      },
      visiblePages() {
        const total = this.totalPages;
        const current = this.configPage;
        
        if (total <= 7) {
             const pages = [];
             for(let i=1; i<=total; i++) pages.push(i);
             return pages;
        }
        
        const pages = [1];
        if (current > 3) pages.push('...');
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
             pages.push(i);
        }
        
        if (current < total - 2) pages.push('...');
        pages.push(total);
        
        return pages;
      }
    },
    created() {
      this.fetchConfigurations();
      this.fetchNiches();
      this.fetchBoardSelection();
    },
    methods: {
      emptyScope() {
        return { board_ids: [], program_ids: [], classes: [] };
      },
      currentSubjectScope() {
        if (this.isCssSelected) {
          return this.emptyScope();
        }
        return {
          board_ids: (this.selectedBoardIds || []).map(Number).filter((n) => Number.isFinite(n) && n > 0),
          program_ids: (this.selectedProgramIds || []).map(Number).filter((n) => Number.isFinite(n) && n > 0),
          classes: (this.selectedClasses || []).map(String).filter((c) => c === '11' || c === '12'),
        };
      },
      resetCssFields() {
        this.subjectType = 'compulsory';
        this.marks = this.CSS_DEFAULT_MARKS;
        this.cssGroup = null;
        this.showInSubjects = true;
      },
      clearFscSelection() {
        this.selectedBoardIds = [];
        this.selectedProgramIds = [];
        this.selectedClasses = [];
        this.showBoardDropdown = false;
        this.showProgramDropdown = false;
        this.showClassDropdown = false;
      },
      onSubjectTypeChange() {
        if (this.subjectType !== 'optional') {
          this.cssGroup = null;
          this.marks = 100;
        } else if (!this.cssMarksOptions.includes(Number(this.marks))) {
          this.marks = this.CSS_DEFAULT_MARKS;
        }
      },
      clampCssMarks() {
        if (this.subjectType === 'compulsory') {
          this.marks = 100;
          return;
        }
        const marksNum = Number(this.marks);
        if (!this.cssMarksOptions.includes(marksNum)) {
          this.marks = this.CSS_DEFAULT_MARKS;
        }
      },
      applyScopeToTopic(topic, scope) {
        this.$set(topic, 'board_ids', [...(scope.board_ids || [])]);
        this.$set(topic, 'program_ids', [...(scope.program_ids || [])]);
        this.$set(topic, 'classes', [...(scope.classes || [])]);
        (topic.subtopics || []).forEach((sub) => {
          if (sub.is_deleted) return;
          this.$set(sub, 'board_ids', [...(scope.board_ids || [])]);
          this.$set(sub, 'program_ids', [...(scope.program_ids || [])]);
          this.$set(sub, 'classes', [...(scope.classes || [])]);
        });
      },
      cascadeSubjectScopeToTopics() {
        const scope = this.currentSubjectScope();
        (this.topics || []).forEach((topic) => {
          if (topic.is_deleted) return;
          this.applyScopeToTopic(topic, scope);
        });
      },
      // Fill only topics/subtopics that have no scope yet (keeps intentional per-topic overrides).
      fillEmptyScopesFromSubject() {
        const scope = this.currentSubjectScope();
        const isEmpty = (entity) => !(
          (entity.board_ids && entity.board_ids.length)
          || (entity.program_ids && entity.program_ids.length)
          || (entity.classes && entity.classes.length)
        );
        (this.topics || []).forEach((topic) => {
          if (topic.is_deleted) return;
          if (isEmpty(topic)) {
            this.$set(topic, 'board_ids', [...scope.board_ids]);
            this.$set(topic, 'program_ids', [...scope.program_ids]);
            this.$set(topic, 'classes', [...scope.classes]);
          }
          (topic.subtopics || []).forEach((sub) => {
            if (sub.is_deleted) return;
            if (isEmpty(sub)) {
              const parentScope = isEmpty(topic) ? scope : {
                board_ids: [...(topic.board_ids || [])],
                program_ids: [...(topic.program_ids || [])],
                classes: [...(topic.classes || [])],
              };
              this.$set(sub, 'board_ids', [...parentScope.board_ids]);
              this.$set(sub, 'program_ids', [...parentScope.program_ids]);
              this.$set(sub, 'classes', [...parentScope.classes]);
            }
          });
        });
      },
      topicOrSubjectScope(topic) {
        const subject = this.currentSubjectScope();
        const hasTopicScope = (
          (topic && topic.board_ids && topic.board_ids.length)
          || (topic && topic.program_ids && topic.program_ids.length)
          || (topic && topic.classes && topic.classes.length)
        );
        if (hasTopicScope) {
          return {
            board_ids: [...(topic.board_ids || [])],
            program_ids: [...(topic.program_ids || [])],
            classes: [...(topic.classes || [])],
          };
        }
        return {
          board_ids: [...subject.board_ids],
          program_ids: [...subject.program_ids],
          classes: [...subject.classes],
        };
      },
      scopeLabel(entity) {
        const boards = (entity && entity.board_ids) || [];
        const programs = (entity && entity.program_ids) || [];
        const classes = (entity && entity.classes) || [];
        if (!boards.length && !programs.length && !classes.length) return 'No FSC scope';
        const boardNames = this.flatBoards
          .filter((b) => boards.map(Number).includes(Number(b.id)))
          .map((b) => b.name);
        const programNames = [];
        this.flatBoards.forEach((b) => {
          (b.programs || []).forEach((p) => {
            if (programs.map(Number).includes(Number(p.id))) programNames.push(p.name);
          });
        });
        const classNames = classes.map((c) => (c === '11' ? '11' : c === '12' ? '12' : c));
        return [
          boardNames.length ? boardNames.join(', ') : null,
          programNames.length ? programNames.join(', ') : null,
          classNames.length ? `Class ${classNames.join('/')}` : null,
        ].filter(Boolean).join(' · ') || 'No FSC scope';
      },
      async fetchBoardSelection() {
        try {
          const response = await quizConfigurationService.getBoardSelection();
          const payload = response && response.data;
          this.boardSelectionTree = (payload && (payload.data || payload.boards)) || [];
        } catch (error) {
          console.error("Error fetching boards/programs:", error);
          this.boardSelectionTree = [];
        }
      },
      toggleBoard(board) {
        const id = Number(board.id);
        const idx = this.selectedBoardIds.findIndex((b) => Number(b) === id);
        if (idx === -1) {
          this.selectedBoardIds.push(id);
        } else {
          this.selectedBoardIds.splice(idx, 1);
          const allowedPrograms = new Set(this.availablePrograms.map((p) => Number(p.id)));
          this.selectedProgramIds = this.selectedProgramIds.filter((pid) => allowedPrograms.has(Number(pid)));
        }
        this.cascadeSubjectScopeToTopics();
      },
      isBoardSelected(board) {
        return this.selectedBoardIds.some((id) => Number(id) === Number(board.id));
      },
      removeBoard(boardId) {
        this.selectedBoardIds = this.selectedBoardIds.filter((id) => Number(id) !== Number(boardId));
        const allowedPrograms = new Set(this.availablePrograms.map((p) => Number(p.id)));
        this.selectedProgramIds = this.selectedProgramIds.filter((pid) => allowedPrograms.has(Number(pid)));
        this.cascadeSubjectScopeToTopics();
      },
      toggleProgram(program) {
        const id = Number(program.id);
        const idx = this.selectedProgramIds.findIndex((p) => Number(p) === id);
        if (idx === -1) this.selectedProgramIds.push(id);
        else this.selectedProgramIds.splice(idx, 1);
        this.cascadeSubjectScopeToTopics();
      },
      isProgramSelected(program) {
        return this.selectedProgramIds.some((id) => Number(id) === Number(program.id));
      },
      removeProgram(programId) {
        this.selectedProgramIds = this.selectedProgramIds.filter((id) => Number(id) !== Number(programId));
        this.cascadeSubjectScopeToTopics();
      },
      toggleClass(classId) {
        const idx = this.selectedClasses.indexOf(classId);
        if (idx === -1) this.selectedClasses.push(classId);
        else this.selectedClasses.splice(idx, 1);
        this.cascadeSubjectScopeToTopics();
      },
      isClassSelected(classId) {
        return this.selectedClasses.includes(classId);
      },
      removeClass(classId) {
        this.selectedClasses = this.selectedClasses.filter((c) => c !== classId);
        this.cascadeSubjectScopeToTopics();
      },
      hideBoardDropdown() { this.showBoardDropdown = false; },
      hideProgramDropdown() { this.showProgramDropdown = false; },
      hideClassDropdown() { this.showClassDropdown = false; },
      toggleTopicBoard(topic, board) {
        if (!topic.board_ids) this.$set(topic, 'board_ids', []);
        const id = Number(board.id);
        const idx = topic.board_ids.findIndex((b) => Number(b) === id);
        if (idx === -1) topic.board_ids.push(id);
        else topic.board_ids.splice(idx, 1);
        const allowed = new Set();
        this.flatBoards.forEach((b) => {
          if (topic.board_ids.map(Number).includes(Number(b.id))) {
            (b.programs || []).forEach((p) => allowed.add(Number(p.id)));
          }
        });
        topic.program_ids = (topic.program_ids || []).filter((pid) => allowed.has(Number(pid)));
        this.cascadeTopicScopeToSubtopics(topic);
      },
      toggleTopicProgram(topic, program) {
        if (!topic.program_ids) this.$set(topic, 'program_ids', []);
        const id = Number(program.id);
        const idx = topic.program_ids.findIndex((p) => Number(p) === id);
        if (idx === -1) topic.program_ids.push(id);
        else topic.program_ids.splice(idx, 1);
        this.cascadeTopicScopeToSubtopics(topic);
      },
      toggleTopicClass(topic, classId) {
        if (!topic.classes) this.$set(topic, 'classes', []);
        const idx = topic.classes.indexOf(classId);
        if (idx === -1) topic.classes.push(classId);
        else topic.classes.splice(idx, 1);
        this.cascadeTopicScopeToSubtopics(topic);
      },
      isTopicBoardSelected(topic, board) {
        return (topic.board_ids || []).some((id) => Number(id) === Number(board.id));
      },
      isTopicProgramSelected(topic, program) {
        return (topic.program_ids || []).some((id) => Number(id) === Number(program.id));
      },
      isTopicClassSelected(topic, classId) {
        return (topic.classes || []).includes(classId);
      },
      topicSelectedBoards(topic) {
        const ids = new Set((topic.board_ids || []).map(Number));
        return this.flatBoards.filter((b) => ids.has(Number(b.id)));
      },
      topicSelectedPrograms(topic) {
        const ids = new Set((topic.program_ids || []).map(Number));
        return this.topicAvailablePrograms(topic).filter((p) => ids.has(Number(p.id)));
      },
      openTopicScopeDropdown(index, field) {
        if (
          this.topicScopeDropdown
          && this.topicScopeDropdown.index === index
          && this.topicScopeDropdown.field === field
        ) {
          this.topicScopeDropdown = null;
          return;
        }
        this.topicScopeDropdown = { index, field };
      },
      isTopicScopeOpen(index, field) {
        return !!(
          this.topicScopeDropdown
          && this.topicScopeDropdown.index === index
          && this.topicScopeDropdown.field === field
        );
      },
      hideTopicScopeDropdown() {
        this.topicScopeDropdown = null;
      },
      topicAvailablePrograms(topic) {
        const selected = new Set((topic.board_ids || []).map(Number));
        const programs = [];
        const seen = new Set();
        this.flatBoards.forEach((board) => {
          if (!selected.has(Number(board.id))) return;
          (board.programs || []).forEach((program) => {
            if (seen.has(Number(program.id))) return;
            seen.add(Number(program.id));
            programs.push({
              ...program,
              board_name: board.name,
            });
          });
        });
        return programs;
      },
      toggleSubtopicBoard(sub, board) {
        if (!sub.board_ids) this.$set(sub, 'board_ids', []);
        const id = Number(board.id);
        const idx = sub.board_ids.findIndex((b) => Number(b) === id);
        if (idx === -1) sub.board_ids.push(id);
        else sub.board_ids.splice(idx, 1);
        const allowed = new Set();
        this.flatBoards.forEach((b) => {
          if (sub.board_ids.map(Number).includes(Number(b.id))) {
            (b.programs || []).forEach((p) => allowed.add(Number(p.id)));
          }
        });
        sub.program_ids = (sub.program_ids || []).filter((pid) => allowed.has(Number(pid)));
      },
      toggleSubtopicProgram(sub, program) {
        if (!sub.program_ids) this.$set(sub, 'program_ids', []);
        const id = Number(program.id);
        const idx = sub.program_ids.findIndex((p) => Number(p) === id);
        if (idx === -1) sub.program_ids.push(id);
        else sub.program_ids.splice(idx, 1);
      },
      toggleSubtopicClass(sub, classId) {
        if (!sub.classes) this.$set(sub, 'classes', []);
        const idx = sub.classes.indexOf(classId);
        if (idx === -1) sub.classes.push(classId);
        else sub.classes.splice(idx, 1);
      },
      isSubtopicBoardSelected(sub, board) {
        return (sub.board_ids || []).some((id) => Number(id) === Number(board.id));
      },
      isSubtopicProgramSelected(sub, program) {
        return (sub.program_ids || []).some((id) => Number(id) === Number(program.id));
      },
      isSubtopicClassSelected(sub, classId) {
        return (sub.classes || []).includes(classId);
      },
      subtopicSelectedBoards(sub) {
        return this.topicSelectedBoards(sub || {});
      },
      subtopicSelectedPrograms(sub) {
        return this.topicSelectedPrograms(sub || {});
      },
      openSubtopicScopeDropdown(index, field) {
        if (
          this.subtopicScopeDropdown
          && this.subtopicScopeDropdown.index === index
          && this.subtopicScopeDropdown.field === field
        ) {
          this.subtopicScopeDropdown = null;
          return;
        }
        this.subtopicScopeDropdown = { index, field };
      },
      isSubtopicScopeOpen(index, field) {
        return !!(
          this.subtopicScopeDropdown
          && this.subtopicScopeDropdown.index === index
          && this.subtopicScopeDropdown.field === field
        );
      },
      hideSubtopicScopeDropdown() {
        this.subtopicScopeDropdown = null;
      },
      subtopicAvailablePrograms(sub) {
        return this.topicAvailablePrograms(sub || {});
      },

      setTopicBoards(topic, event) {
        topic.board_ids = Array.from(event.target.selectedOptions).map((o) => Number(o.value));
        const allowed = new Set(this.topicAvailablePrograms(topic).map((p) => Number(p.id)));
        topic.program_ids = (topic.program_ids || []).filter((pid) => allowed.has(Number(pid)));
        this.cascadeTopicScopeToSubtopics(topic);
      },
      setTopicPrograms(topic, event) {
        topic.program_ids = Array.from(event.target.selectedOptions).map((o) => Number(o.value));
        this.cascadeTopicScopeToSubtopics(topic);
      },
      setTopicClasses(topic, event) {
        topic.classes = Array.from(event.target.selectedOptions).map((o) => o.value);
        this.cascadeTopicScopeToSubtopics(topic);
      },
      cascadeTopicScopeToSubtopics(topic) {
        const scope = {
          board_ids: [...(topic.board_ids || [])],
          program_ids: [...(topic.program_ids || [])],
          classes: [...(topic.classes || [])],
        };
        (topic.subtopics || []).forEach((sub) => {
          if (sub.is_deleted) return;
          this.$set(sub, 'board_ids', [...scope.board_ids]);
          this.$set(sub, 'program_ids', [...scope.program_ids]);
          this.$set(sub, 'classes', [...scope.classes]);
        });
      },
      setSubtopicBoards(sub, event) {
        sub.board_ids = Array.from(event.target.selectedOptions).map((o) => Number(o.value));
        const allowed = new Set(this.subtopicAvailablePrograms(sub).map((p) => Number(p.id)));
        sub.program_ids = (sub.program_ids || []).filter((pid) => allowed.has(Number(pid)));
      },
      setSubtopicPrograms(sub, event) {
        sub.program_ids = Array.from(event.target.selectedOptions).map((o) => Number(o.value));
      },
      setSubtopicClasses(sub, event) {
        sub.classes = Array.from(event.target.selectedOptions).map((o) => o.value);
      },

      async fetchConfigurations(page = 1) {
        try {
          this.isLoading = true;
          this.configPage = page;
          const limit = this.configLimit || 10;
          
          const response = await quizConfigurationService.getAllConfigurations("", this.searchQuery, true, page, limit);
          
          if (response && response.data) {
             this.configurations = response.data.docs || [];
             this.totalDocs = response.data.totalDocs || 0;
             this.totalPages = response.data.totalPages || 1;
             this.configPage = response.data.page || page;
          } else {
             this.configurations = [];
             this.totalDocs = 0;
             this.totalPages = 1;
          }
        } catch (error) {
          console.error("Error fetching configurations:", error);
        } finally {
          this.isLoading = false;
        }
      },

      changePage(page) {
        if (page < 1 || page > this.totalPages || page === this.configPage) return;
        this.fetchConfigurations(page);
      },
      
      debouncedSearch: _.debounce(function() {
         this.configPage = 1;
         this.fetchConfigurations(1);
      }, 500),

      async editConfig (config) {
        this.isEditMode = true;
        this.editingConfigId = config._id;
        this.configName = config.name;
        this.isOpen = true;
        this.currentStep = 1;
        this.isFetchingConfig = true; // Start loading
        
        try {
          const response = await quizConfigurationService.getConfiguration(config._id);
          if (response.data && response.data.configuration) {
            const configData = response.data.configuration;
            
            // Populate Base Configuration
            if (configData.base_configuration) {
              this.baseConfiguration = configData.base_configuration.name;
            } else {
              this.baseConfiguration = "";
            }

            // Populate Niche (multi-select)
            if (configData.niche_id) {
              // niche_id can be a single id or an array of ids
              const nicheIds = Array.isArray(configData.niche_id) 
                ? configData.niche_id 
                : [configData.niche_id];
              this.selectedNiches = this.niches.filter(n => nicheIds.includes(n.id));
            } else {
              this.selectedNiches = [];
            }

            this.selectedBoardIds = (configData.board_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0);
            this.selectedProgramIds = (configData.program_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0);
            this.selectedClasses = (configData.classes || []).map(String).filter((c) => c === '11' || c === '12');

            // CSS subject fields
            {
              const allowedTypes = this.cssSubjectTypeOptions.map((o) => o.value);
              const loadedType = configData.subject_type != null
                ? String(configData.subject_type).trim().toLowerCase()
                : 'compulsory';
              this.subjectType = allowedTypes.includes(loadedType) ? loadedType : 'compulsory';
            }
            if (this.subjectType === 'compulsory') {
              this.marks = 100;
              this.cssGroup = null;
            } else {
              const loadedMarks = configData.marks != null ? Number(configData.marks) : this.CSS_DEFAULT_MARKS;
              this.marks = this.cssMarksOptions.includes(loadedMarks)
                ? loadedMarks
                : this.CSS_DEFAULT_MARKS;
              this.cssGroup = configData.group != null ? Number(configData.group) : null;
            }
            this.showInSubjects = configData.show_in_subjects !== false;

            this.topics = configData.topics.map(topic => ({
              _id: topic._id,
              name: topic.name,
              is_deleted: false,
              board_ids: (topic.board_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0),
              program_ids: (topic.program_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0),
              classes: (topic.classes || []).map(String).filter((c) => c === '11' || c === '12'),
              subtopics: topic.sub_topics ? topic.sub_topics.map(sub => ({
                _id: sub._id,
                name: sub.name,
                is_deleted: false,
                board_ids: (sub.board_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0),
                program_ids: (sub.program_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0),
                classes: (sub.classes || []).map(String).filter((c) => c === '11' || c === '12'),
                learning_outcomes: sub.learning_outcomes ? sub.learning_outcomes.map(lo => ({
                    _id: null,
                    name: lo, 
                    is_deleted: false
                })) : []
              })) : []
            }));
            // Older topics may have no scope saved — inherit from subject so pills show selected.
            this.fillEmptyScopesFromSubject();
          }
        } catch (error) {
          console.error("Error fetching configuration details:", error);
        } finally {
          this.isFetchingConfig = false; // Stop loading
        }
      },

      async deleteConfig (config) {
        if (confirm(`Are you sure you want to delete "${config.name}"?`)) {
          try {
            await quizConfigurationService.deleteConfiguration(config._id, {
              deleted_by: storageService.get("uid") 
            });
            this.fetchConfigurations();
          } catch (error) {
            console.error("Error deleting configuration:", error);
          }
        }
      },
       openModal () {
        this.isEditMode = false;
        this.editingConfigId = null;
        this.configName = '';
        this.baseConfiguration = '';
        this.selectedNiches = [];
        this.selectedBoardIds = [];
        this.selectedProgramIds = [];
        this.selectedClasses = [];
        this.resetCssFields();
        this.topics = []; 
        this.isOpen = true;
        this.currentStep = 1;
      },
      closeModal () {
        this.isOpen = false
      },
      nextStep () {
        // When moving to topics step, apply subject Board/Program/Class as the default selection.
        this.cascadeSubjectScopeToTopics();
        this.currentStep = 2;
      },
  
      prevStep () {
        this.currentStep = 1
      },

      async fetchNiches() {
        try {
          const response = await quizConfigurationService.getCourseTags();
          if (response && response.data && response.data.success) {
            this.niches = response.data.courseTags || [];
          }
        } catch (error) {
          console.error("Error fetching niches:", error);
        }
      },

      toggleNiche(niche) {
        if (this.isNicheDisabled(niche)) return;

        const isCssNiche = Number(niche.id) === this.CSS_NICHE_ID;
        const index = this.selectedNiches.findIndex(n => n.id === niche.id);

        if (index !== -1) {
          this.selectedNiches.splice(index, 1);
          if (isCssNiche) {
            this.resetCssFields();
          }
          return;
        }

        if (isCssNiche) {
          // CSS cannot be combined with any other niche
          this.selectedNiches = [niche];
          this.clearFscSelection();
          this.resetCssFields();
          return;
        }

        // Selecting a non-CSS niche removes CSS if present
        this.selectedNiches = this.selectedNiches.filter(
          (n) => Number(n.id) !== this.CSS_NICHE_ID,
        );
        this.selectedNiches.push(niche);
        this.resetCssFields();
      },

      isNicheSelected(niche) {
        return this.selectedNiches.some(n => n.id === niche.id);
      },

      isNicheDisabled(niche) {
        const isCssNiche = Number(niche.id) === this.CSS_NICHE_ID;
        // CSS selected → all other niches disabled
        if (this.isCssSelected && !isCssNiche) return true;
        // Any non-CSS niche selected → CSS disabled
        if (this.hasNonCssNicheSelected && isCssNiche) return true;
        return false;
      },

      removeNiche(niche) {
        const index = this.selectedNiches.findIndex(n => n.id === niche.id);
        if (index !== -1) {
          this.selectedNiches.splice(index, 1);
        }
        if (Number(niche.id) === this.CSS_NICHE_ID) {
          this.resetCssFields();
        }
      },

      hideNicheDropdown() {
        this.showNicheDropdown = false;
      },
  
      handleTopicInput (e) {
        if (e.key === 'Enter' || e.key === ',') {
          e.preventDefault()
          const value = this.topicInput.trim().replace(',', '')
          if (value) {
            const scope = this.currentSubjectScope();
            this.topics.push({
              name: value,
              subtopics: [],
              is_deleted: false,
              board_ids: [...scope.board_ids],
              program_ids: [...scope.program_ids],
              classes: [...scope.classes],
            })
            this.topicInput = ''
          }
        }
      },
  

      removeTopic (index) {
        if (this.topics[index]._id) {
          this.topics[index].is_deleted = true;
        } else {
          this.topics.splice(index, 1)
        }
      },

      removeSubtopic (topicIndex, subtopicIndex) {
        if (this.topics[topicIndex].subtopics[subtopicIndex]._id) {
          this.topics[topicIndex].subtopics[subtopicIndex].is_deleted = true;
        } else {
          this.topics[topicIndex].subtopics.splice(subtopicIndex, 1);
        }
      },

      // Topic Inline Edit Methods
      startTopicEdit(index) {
        this.editingTopicIndex = index;
        this.editingTopicName = this.topics[index].name;
        this.$nextTick(() => {
          if (this.$refs.topicEditInput && this.$refs.topicEditInput[0]) {
            this.$refs.topicEditInput[0].focus();
          }
        });
      },
      cancelTopicEdit() {
        this.editingTopicIndex = null;
        this.editingTopicName = '';
      },
      async saveTopicEdit(index) {
        const newName = this.editingTopicName.trim();
        if (!newName) return;

        const topic = this.topics[index];
        topic.name = newName;

        this.editingTopicIndex = null;
        this.editingTopicName = '';
      },

      // Subtopic Modal & Edit Methods
      openSubtopicModal(index) {
        this.selectedTopicIndex = index;
        this.selectedTopicName = this.topics[index].name;
        this.isEditingSubtopics = !!this.topics[index].subtopics.length;
        // Ensure topic has subject scope before copying into subtopic editor.
        const topic = this.topics[index];
        const inherited = this.topicOrSubjectScope(topic);
        if (!(topic.board_ids && topic.board_ids.length) && !(topic.program_ids && topic.program_ids.length) && !(topic.classes && topic.classes.length)) {
          this.$set(topic, 'board_ids', [...inherited.board_ids]);
          this.$set(topic, 'program_ids', [...inherited.program_ids]);
          this.$set(topic, 'classes', [...inherited.classes]);
        }
        // Deep copy subtopics to temp array to allow cancellation
        this.tempSubtopics = JSON.parse(JSON.stringify(this.topics[index].subtopics.filter(s => !s.is_deleted))).map((sub) => {
          const empty = !(
            (sub.board_ids && sub.board_ids.length)
            || (sub.program_ids && sub.program_ids.length)
            || (sub.classes && sub.classes.length)
          );
          if (!empty) return sub;
          return {
            ...sub,
            board_ids: [...inherited.board_ids],
            program_ids: [...inherited.program_ids],
            classes: [...inherited.classes],
          };
        });
        this.showSubtopicModal = true;
        this.subtopicInput = '';
      },
      closeSubtopicModal() {
        this.showSubtopicModal = false;
        this.selectedTopicIndex = null;
        this.subtopicInput = '';
        this.tempSubtopics = [];
        this.subtopicScopeDropdown = null;
      },
      handleSubtopicInput(e) {
        if (e.key === 'Enter' || e.key === ',') {
          e.preventDefault()
          this.addSubtopicFromButton();
        }
      },
      addSubtopicFromButton() {
        const value = this.subtopicInput.trim().replace(',', '')
        if (value) {
          const topic = this.topics[this.selectedTopicIndex] || {};
          const scope = this.topicOrSubjectScope(topic);
          this.tempSubtopics.push({
            name: value,
            is_deleted: false,
            board_ids: [...scope.board_ids],
            program_ids: [...scope.program_ids],
            classes: [...scope.classes],
          })
          this.subtopicInput = ''
        }
      },
      removeTempSubtopic(index) {
        // If it has an ID, mark as deleted (but we are working on temp array, so we need to track deletions)
        // For simplicity in this modal flow, we can just remove from temp array if it's new.
        // If it's existing (has ID), we should probably mark it. 
        // However, the user asked to "integrate APIs". 
        // Let's stick to the pattern: modify temp, then sync back on save.
        
        const subtopic = this.tempSubtopics[index];
        if (subtopic._id) {
           // We can't just remove it from temp array if we want to track deletion for backend.
           // But the UI shows a list of *visible* subtopics.
           // So we remove from temp array, and when saving, we compare with original to find deletions.
           this.tempSubtopics.splice(index, 1);
        } else {
           this.tempSubtopics.splice(index, 1);
        }
      },

      startSubtopicEdit(index) {
        this.editingSubtopicIndex = index;
        this.editingSubtopicName = this.tempSubtopics[index].name;
      },
      cancelSubtopicEdit() {
        this.editingSubtopicIndex = null;
        this.editingSubtopicName = '';
      },
      saveSubtopicEdit(index) {
        const newName = this.editingSubtopicName.trim();
        if (newName) {
          this.tempSubtopics[index].name = newName;
        }
        this.editingSubtopicIndex = null;
        this.editingSubtopicName = '';
      },

      async saveSubtopics() {
        if (this.selectedTopicIndex === null) return;

        const topic = this.topics[this.selectedTopicIndex];
        const originalSubtopics = topic.subtopics;
        
        // 1. Identify deletions: items in original (with ID) that are NOT in temp
        const tempIds = new Set(this.tempSubtopics.filter(s => s._id).map(s => s._id));
        const deletedSubtopics = originalSubtopics.filter(s => s._id && !tempIds.has(s._id));

        // 2. Identify updates: items in temp (with ID) that have changed name
        const updates = this.tempSubtopics.filter(s => s._id);

        // 3. Identify new: items in temp (no ID)
        const newSubtopics = this.tempSubtopics.filter(s => !s._id);

        // Apply changes to local state immediately
        // We need to keep the deleted ones in the main array but marked is_deleted
        const finalSubtopics = [];
        
        // Add kept/updated existing ones
        updates.forEach(u => finalSubtopics.push(u));
        
        // Add new ones
        newSubtopics.forEach(n => finalSubtopics.push(n));
        
        // Add deleted ones (marked)
        deletedSubtopics.forEach(d => {
          d.is_deleted = true;
          finalSubtopics.push(d);
        });

        topic.subtopics = finalSubtopics;

        topic.subtopics = finalSubtopics;

        this.closeSubtopicModal();
      },

      // Learning Outcomes Methods
      openLearningOutcomeModal(index) {
          // index is the index in tempSubtopics
          this.selectedSubtopicIndex = index;
          this.selectedSubtopicName = this.tempSubtopics[index].name;
          
          const subtopic = this.tempSubtopics[index];
          // Initialize learning_outcomes if not present
          if (!subtopic.learning_outcomes) {
              this.$set(subtopic, 'learning_outcomes', []);
          }
          
          // Deep copy for temp editing
          this.tempLearningOutcomes = JSON.parse(JSON.stringify(subtopic.learning_outcomes.filter(lo => !lo.is_deleted)));
          
          this.showLearningOutcomeModal = true;
          this.learningOutcomeInput = '';
      },
      closeLearningOutcomeModal() {
          this.showLearningOutcomeModal = false;
          this.selectedSubtopicIndex = null;
          this.tempLearningOutcomes = [];
          // Reset equation mode
          this.learningOutcomeEquationMode = false;
          this.learningOutcomeEquation = '';
      },
      handleLearningOutcomeInput(e) {
          if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              this.addLearningOutcomeFromButton();
          }
      },
      addLearningOutcomeFromButton() {
          const textValue = this.learningOutcomeInput.trim().replace(',', '');
          const equationValue = this.learningOutcomeEquation.trim();
          
          let finalValue = '';
          if (textValue && equationValue) {
              // Combine text and equation - add spaces for safety
              finalValue = `${textValue} {{ ${equationValue} }}`;
          } else if (equationValue) {
              // Equation only - add spaces for safety
              finalValue = `{{ ${equationValue} }}`;
          } else if (textValue) {
              // Text only
              finalValue = textValue;
          }
          
          if (finalValue) {
              this.tempLearningOutcomes.push({ name: finalValue, is_deleted: false });
              this.learningOutcomeInput = '';
              this.learningOutcomeEquation = '';
              this.learningOutcomeEquationMode = false;
          }
      },
      removeTempLearningOutcome(index) {
          const lo = this.tempLearningOutcomes[index];
          if (lo._id) {
              // Mark as deleted in our temp list logic (similar to subtopics)
              // But for UI, we just remove from view.
              // We'll handle the diffing in saveLearningOutcomes
              this.tempLearningOutcomes.splice(index, 1); 
          } else {
              this.tempLearningOutcomes.splice(index, 1);
          }
      },
      startLearningOutcomeEdit(index) {
          this.editingLearningOutcomeIndex = index;
          this.editingLearningOutcomeName = this.tempLearningOutcomes[index].name;
      },
      cancelLearningOutcomeEdit() {
          this.editingLearningOutcomeIndex = null;
          this.editingLearningOutcomeName = '';
      },
      saveLearningOutcomeEdit(index) {
          let newName = this.editingLearningOutcomeName.trim();
          const equationValue = this.editingLOEquation.trim();
          
          // If editing with equation mode on, update the equation portion
          if (this.editingLOEquationMode && equationValue) {
              // Remove existing equation from the name first
              newName = newName.replace(/\{\{[^}]+\}\}/g, '').trim();
              // Add the new equation
              if (newName) {
                  newName = `${newName} {{${equationValue}}}`;
              } else {
                  newName = `{{${equationValue}}}`;
              }
          }
          
          if (newName) {
              this.tempLearningOutcomes[index].name = newName;
          }
          this.editingLearningOutcomeIndex = null;
          this.editingLearningOutcomeName = '';
          this.editingLOEquationMode = false;
          this.editingLOEquation = '';
      },
      saveLearningOutcomes() {
          if (this.selectedSubtopicIndex === null) return;
          
          const subtopic = this.tempSubtopics[this.selectedSubtopicIndex];
          const originalLOs = subtopic.learning_outcomes || []; // Should be initialized in open
          
          // Diffing logic similar to subtopics
          const tempIds = new Set(this.tempLearningOutcomes.filter(lo => lo._id).map(lo => lo._id));
          const deletedLOs = originalLOs.filter(lo => lo._id && !tempIds.has(lo._id));
          const updates = this.tempLearningOutcomes.filter(lo => lo._id);
          const newLOs = this.tempLearningOutcomes.filter(lo => !lo._id);
          
          const finalLOs = [];
          updates.forEach(u => finalLOs.push(u));
          newLOs.forEach(n => finalLOs.push(n));
          deletedLOs.forEach(d => {
              d.is_deleted = true;
              finalLOs.push(d);
          });
          
          subtopic.learning_outcomes = finalLOs;
          this.closeLearningOutcomeModal();
      },

      // MathQuill Methods for Learning Outcomes
      toggleLOEquationMode() {
          this.learningOutcomeEquationMode = !this.learningOutcomeEquationMode;
          if (this.learningOutcomeEquationMode) {
              this.$nextTick(() => {
                  this.initLOMathQuill('lo-equation-input', '');
              });
          } else {
              this.learningOutcomeEquation = '';
          }
      },

      initLOMathQuill(elementId, initialLatex) {
          setTimeout(() => {
              const mathFieldSpan = document.getElementById(elementId);
              if (!mathFieldSpan) return;
              
              const MQ = MathQuill.getInterface(2);
              const self = this;
              const mathField = MQ.MathField(mathFieldSpan, {
                  spaceBehavesLikeTab: false,
                  handlers: {
                      edit: function() {
                          if (elementId === 'lo-equation-input') {
                              self.learningOutcomeEquation = mathField.latex();
                          } else if (elementId === 'lo-equation-edit') {
                              self.editingLOEquation = mathField.latex();
                          }
                      }
                  }
              });
              
              if (initialLatex && initialLatex.trim()) {
                  let latex = initialLatex;
                  if (!latex.match(/\\\s/g)) {
                      latex = latex.replace(/\s+/g, '\\ ');
                  }
                  mathField.latex(latex);
              }
          }, 10);
      },

      parseLearningOutcome(loName) {
          // Parse learning outcome to extract text and equation parts using brace counting
          // This allows handling nested braces in LaTeX correctly, e.g. \frac{a}{b}
          const parts = [];
          let currentIndex = 0;
          
          while (currentIndex < loName.length) {
              const openIndex = loName.indexOf('{{', currentIndex);
              
              if (openIndex === -1) {
                  // No more equations, add rest as text
                  parts.push({ type: 'text', content: loName.substring(currentIndex) });
                  break;
              }
              
              // Add text before the equation
              if (openIndex > currentIndex) {
                  parts.push({ type: 'text', content: loName.substring(currentIndex, openIndex) });
              }
              
              // Find matching closing }}
              let depth = 0;
              let closeIndex = -1;
              const contentStart = openIndex + 2;
              
              for (let i = contentStart; i < loName.length; i++) {
                  const char = loName[i];
                  
                  if (char === '{') {
                      depth++;
                  } else if (char === '}') {
                      if (depth === 0) {
                          // Check if this is the start of }}
                          if (loName[i+1] === '}') {
                              closeIndex = i;
                              break;
                          }
                      } else {
                          depth--;
                      }
                  }
              }
              
              if (closeIndex !== -1) {
                  const equationContent = loName.substring(contentStart, closeIndex);
                  // console.log('[LO PARSE MANUAL] Found equation:', equationContent);
                  parts.push({ type: 'equation', content: equationContent });
                  currentIndex = closeIndex + 2;
              } else {
                  // No closing braces found, treat rest as text
                  parts.push({ type: 'text', content: loName.substring(openIndex) });
                  break;
              }
          }
           
          // Filter out empty text parts if any
          return parts.filter(p => !(p.type === 'text' && !p.content));
      },
      hasEquation(loName) {
          return loName && loName.includes('{{') && loName.includes('}}');
      },

      renderStaticMath(elementId, latex) {
          this.$nextTick(() => {
              setTimeout(() => {
                  const el = document.getElementById(elementId);
                  if (el && !el.dataset.mqInitialized) {
                      try {
                          const MQ = MathQuill.getInterface(2);
                          // Clear the element first
                          el.innerHTML = '';
                          // Create StaticMath and set latex
                          const staticMath = MQ.StaticMath(el);
                          // Process the latex (handle spaces like mathCreator does)
                          let processedLatex = latex;
                          if (!processedLatex.match(/\\\s/g)) {
                              processedLatex = processedLatex.replace(/\s+/g, '\\ ');
                          }
                          staticMath.latex(processedLatex);
                          el.dataset.mqInitialized = 'true';
                      } catch (e) {
                          console.warn('MathQuill init failed for', elementId, e);
                          // Fallback: just show the raw latex
                          el.textContent = latex;
                      }
                  }
              }, 100);
          });
          return ''; // Return empty string for template compatibility
      },

      toggleEditLOEquationMode(index) {
          if (this.editingLearningOutcomeIndex !== index) {
              this.startLearningOutcomeEdit(index);
          }
          this.editingLOEquationMode = !this.editingLOEquationMode;
          if (this.editingLOEquationMode) {
              // Extract existing equation if any
              const loName = this.tempLearningOutcomes[index].name;
              const match = loName.match(/\{\{([^}]+)\}\}/);
              this.editingLOEquation = match ? match[1] : '';
              this.$nextTick(() => {
                  this.initLOMathQuill('lo-equation-edit', this.editingLOEquation);
              });
          } else {
              this.editingLOEquation = '';
          }
      },
  
      async saveConfiguration () {
        if (this.isSaving) {
          return;
        }

        const userId = storageService.get("uid"); 
        const subjectScope = this.currentSubjectScope();
        const isCss = this.isCssSelected;
        
        const payload = {
          name: this.configName,
          base_configuration: this.baseConfiguration || undefined,
          niche_id: isCss
            ? [this.CSS_NICHE_ID]
            : (this.selectedNiches.length > 0 ? this.selectedNiches.map(n => n.id) : undefined),
          board_ids: isCss ? [] : subjectScope.board_ids,
          program_ids: isCss ? [] : subjectScope.program_ids,
          classes: isCss ? [] : subjectScope.classes,
          created_by: userId,
          topics: this.topics.map(topic => ({
            _id: topic._id,
            name: topic.name,
            is_deleted: topic.is_deleted,
            board_ids: isCss ? [] : (topic.board_ids || []),
            program_ids: isCss ? [] : (topic.program_ids || []),
            classes: isCss ? [] : (topic.classes || []),
            sub_topics: topic.subtopics.map(sub => ({
              _id: sub._id,
              name: sub.name,
              is_deleted: sub.is_deleted,
              board_ids: isCss ? [] : (sub.board_ids || []),
              program_ids: isCss ? [] : (sub.program_ids || []),
              classes: isCss ? [] : (sub.classes || []),
              learning_outcomes: sub.learning_outcomes ? sub.learning_outcomes
                  .filter(lo => !lo.is_deleted) // Only send non-deleted ones
                  .map(lo => lo.name) // Send just the string
                  : [] 
            }))
          }))
        };

        if (isCss) {
          payload.subject_type = this.subjectType;
          payload.marks = this.subjectType === 'compulsory' ? 100 : Number(this.marks);
          payload.group = this.subjectType === 'optional' ? Number(this.cssGroup) : null;
          payload.show_in_subjects = this.showInSubjects !== false;
        }

        this.isSaving = true;
        try {
          if (this.isEditMode) {
            await quizConfigurationService.updateConfiguration(this.editingConfigId, payload);
          } else {
            await quizConfigurationService.createConfiguration(payload);
          }
          this.fetchConfigurations();
          this.closeModal();
        } catch (error) {
          console.error("Error saving configuration:", error);
          const errorMessage = (error.response && error.response.data && error.response.data.message)
            || error.message
            || "Failed to save configuration";
          this.$toast.error(errorMessage);
        } finally {
          this.isSaving = false;
        }
      },
    },
  };
</script>

<style src="./quiz-configration.css" scoped></style>
<style src="./config-loader.css" scoped></style>

